import { Link } from "react-router-dom";
import { MessageCircle } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { PageHero, usePageTitle } from "../components/Shared";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { WHATSAPP_URL } from "../data/content";
import { useLang, useContent } from "../i18n";

const COPY = {
  fr: {
    eyebrow: "Questions fréquentes",
    h1a: "Tout savoir avant",
    h1b: "de vous lancer.",
    sub: "Démarches, documents, délais, tarifs, fonctionnement : les réponses aux questions que l'on nous pose le plus souvent.",
    sideTitle: "Une autre question ?",
    sideText: "Notre équipe répond sous 24h ouvrées. Pour une réponse immédiate, écrivez-nous directement sur WhatsApp.",
    sideWhatsapp: "Discuter sur WhatsApp",
    sideCta: "Demander une démarche",
  },
  en: {
    eyebrow: "Frequently asked questions",
    h1a: "Everything to know",
    h1b: "before you start.",
    sub: "Procedures, documents, timelines, pricing, how it works: answers to the questions we are asked most often.",
    sideTitle: "Another question?",
    sideText: "Our team replies within 24 business hours. For an immediate answer, write to us directly on WhatsApp.",
    sideWhatsapp: "Chat on WhatsApp",
    sideCta: "Start a request",
  },
};

export default function Faq() {
  const { lang } = useLang();
  const { FAQ_ITEMS } = useContent();
  const c = COPY[lang];
  usePageTitle("FAQ");
  return (
    <div data-testid="faq-page">
      <PageHero eyebrow={c.eyebrow} lines={[<>{c.h1a}</>, <>{c.h1b}</>]} sub={c.sub} />
      <section className="bg-ivory py-24 sm:py-28">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-6 lg:grid-cols-12">
          <Reveal className="lg:col-span-8">
            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {FAQ_ITEMS.map((item, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  className="mb-4 rounded-2xl border border-brand/10 bg-white px-6 shadow-sm data-[state=open]:border-gold/40"
                >
                  <AccordionTrigger
                    data-testid={`faq-question-${i}`}
                    className="py-5 text-left font-display text-base font-bold text-slate-900 hover:text-brand hover:no-underline lg:text-lg"
                  >
                    <span className="flex items-baseline gap-4">
                      <span className="font-mono text-xs font-bold text-gold">{String(i + 1).padStart(2, "0")}</span>
                      {item.q}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 pl-9 text-sm leading-relaxed text-slate-600 lg:text-base">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-4">
            <div className="sticky top-28 rounded-3xl bg-brand-deep p-8 text-white">
              <h3 className="font-display text-xl font-bold">{c.sideTitle}</h3>
              <p className="mt-3 text-sm leading-relaxed text-white/60">{c.sideText}</p>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="faq-whatsapp-btn"
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-3.5 font-display font-bold text-white transition-[background-color,transform] duration-300 hover:bg-[#1fb857] hover:-translate-y-0.5"
              >
                <MessageCircle className="h-5 w-5" />
                {c.sideWhatsapp}
              </a>
              <Link
                to="/contact"
                data-testid="faq-contact-link"
                className="mt-3 inline-flex w-full items-center justify-center rounded-full border border-white/25 px-6 py-3.5 font-display font-bold text-white transition-[background-color] duration-300 hover:bg-white/10"
              >
                {c.sideCta}
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
